import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Collapse, Grid, Paper, Skeleton, Step, Stepper, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';

import {
  getFlightOfferBookingDetails,
  requestPayment,
  setBookingStepToAddBaggage,
  setBookingStepToPassengerDetailsCapturing,
  setBookingStepToPaymentRequested
} from '~/actions';
import { BOOKING_STEP, STATUS, NAVIGATOR_ROUTES, BOOKING_CONTEXT, ERROR_MESSAGE } from '~/literals';

import StandardPage from '~/layouts/StandardPage';
import {
  FlightBookingPostParams,
  FlightOfferPriceCheckResponse,
  Passenger,
  RootState,
  FlightBookingPostParamsValidationMessage,
  ExtraBags,
  FlightBookingState,
  PaymentRequest,
  Leg,
  Bag,
} from '~/types';
import TravelerContactDetails from '~/components/Form/TravelerContactDetails';
import { Box, Theme } from '@mui/system';
import Button from '@mui/material/Button';
import PassengerDetails from '~/components/Form/PassengerDetails';
import { allBookingFieldsValid, deepCopy } from '~/modules/helpers';
import FlightBookingPriceSummary from '~/components/FlightBookingPriceSummary';
import { getBookingPostParams, getBookingValidationMessages } from "~/modules/flightBookingFormHelper";
import { Flight } from '@mui/icons-material';
import FlightCardItemDetailsCollapsible from '~/components/FlightCardItemDetailsCollapsible';
import PageNotFound from '~/components/PageNotFound';
import BookingTermsAndConditions from '~/components/BookingTermsAndConditions';
import FairRulesModal from '~/components/FairRulesModal';
import { useMediaQuery } from '@mui/material';
import AddBaggageToBookingSingleSelector from '~/components/AddBaggageToBookingSingleSelector';


function FlightBooking() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const status = useSelector<RootState, string>(({ flightBooking }) => flightBooking.pageStatus);
  const bookingStep = useSelector<RootState, string>(({ flightBooking }) => flightBooking.bookingStep);
  const paymentRequest = useSelector<RootState, PaymentRequest | null>(({ flightBooking }) => flightBooking.paymentRequest);
  const errorMessage = useSelector<RootState, string | undefined>(({ flightBooking }) => flightBooking.errorMessage);
  //  const receivedFromSearchResultsPage = useSelector<RootState, Boolean>(({ flightBooking }) => flightBooking.receivedFromSearchResultsPage);
  const flightBookingState: FlightBookingState = useSelector<RootState, FlightBookingState>(({ flightBooking }) => flightBooking) as FlightBookingState;
  const flightBookingDetails = useSelector<RootState, FlightOfferPriceCheckResponse | null>(({ flightBooking }) => flightBooking.offerBookingDetails);

  const [validationMode, setValidationMode] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [validationMessages, setValidationMessages] = useState<FlightBookingPostParamsValidationMessage>({
    tripContactPersons: [],
    passengers: [],
    globalErrorMessages: [],
    termsAccepted: [],
  });

  const [bookingParams, setBookingParams] = useState<FlightBookingPostParams>(getBookingPostParams(flightBookingDetails));

  const [isFareRulesModalOpen, setFareRulesModalOpen] = useState(false);

  const extraBaggageOffersAvailable = flightBookingDetails?.response.upsellExtras?.bags && bookingParams.passengers;

  useEffect(() => {
    if (location?.state?.from !== NAVIGATOR_ROUTES.OfferList
      || performance.getEntriesByType('navigation')[0].type === 'reload'
    ) {
      dispatch(
        getFlightOfferBookingDetails({
          id: id || '',
        }),
      );
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (flightBookingDetails) {
      setBookingParams(getBookingPostParams(flightBookingDetails));
    }
  }, [flightBookingDetails]);

  useEffect(() => {
    if (bookingStep === BOOKING_STEP.PAYMENT_REQUESTED && paymentRequest) {
      navigate(`/flights/payment/${paymentRequest.bookingId}`);
    }
    // TODO: For now if a payment error happens, user should start the search again. In the future we should have optiont to return to booking details page.
    else if (bookingStep === BOOKING_STEP.PAYMENT_REQUESTED && errorMessage && status === STATUS.ERROR) {
      navigate(`/flights/payment/${id}`);
    }
  }, [bookingStep, paymentRequest, status, errorMessage]);

  const isLastStep = () => {
    return !(extraBaggageOffersAvailable &&
      flightBookingDetails.bookingContext !== BOOKING_CONTEXT.FLIGHT_ITINERARY &&
      bookingStep !== BOOKING_STEP.BAGGAGE_ADDITION);
  }

  const handleFareRulesOpenModal = () => {
    // Track fare rules modal open click
    ReactGA.event({
      category: 'FlightBooking',
      action: 'Flight Booking Fare Rules Modal Opened',
      label: `booking_${id}`,
    });

    setFareRulesModalOpen(true);
  };

  const handleFareRulesCloseModal = () => {
    setFareRulesModalOpen(false);
  };

  const handlePassengerFieldChange = (name: string, value: string, passengerID: string) => {
    const nameLetters = name.match(/[a-zA-Z]/g);
    const fieldToUpdate = nameLetters ? nameLetters.join('') : '';
    const copiedBookingParams = deepCopy(bookingParams);
    copiedBookingParams.passengers?.map((passenger: Passenger) => {
      if (passenger.id === passengerID) {
        if (['nationality', 'documentType', 'number', 'expiryDate'].includes(fieldToUpdate) && passenger.documents?.length) {
          if (fieldToUpdate === 'nationality') {
            passenger.documents[0].nationality.code = value;
          }
          else {
            passenger.documents[0] = {
              ...passenger.documents[0],
              [fieldToUpdate]: value,
            }
          }
        } else {
          passenger[fieldToUpdate] = value;
        }
      }
    });

    setValidationMessages(getBookingValidationMessages(copiedBookingParams, flightBookingDetails?.bookingContext || ''));
    setBookingParams(copiedBookingParams);
  }

  const handleTermsAccept = (accepted: boolean) => {
    const copiedBookingParams = deepCopy(bookingParams);
    copiedBookingParams.termsAccepted = accepted;

    setValidationMessages(getBookingValidationMessages(copiedBookingParams, flightBookingDetails?.bookingContext || ''));
    setBookingParams(copiedBookingParams);
  };

  const handleContactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const copiedBookingParams = deepCopy(bookingParams);

    copiedBookingParams.tripContactPersons?.map(contactPerson => {
      let contactDetails = contactPerson.contactDetails;
      if (contactDetails) {
        if (['countryCode', 'number'].includes(name)) {
          contactDetails.phoneNumbers[0][name] = value;
        } else if ('country' === name && contactDetails.country) {
          contactDetails.country.code = value;
        } else if ('city' == name && contactDetails.city) {
          contactDetails.city.friendlyName = value;
        } else {
          contactDetails[name] = name === 'addressLines' ? [value] : value;
        }
      }
    });

    setValidationMessages(getBookingValidationMessages(copiedBookingParams, flightBookingDetails?.bookingContext || ''));
    setBookingParams(copiedBookingParams);
  }

  const handleBookingDetailsSubmitted = () => {
    const messages = getBookingValidationMessages(bookingParams, flightBookingDetails?.bookingContext || '');
    setValidationMessages(messages);
    window.scrollTo(0, 0);
    // Check if all booking fields are valid, except for terms and conditions
    if (!allBookingFieldsValid(messages)) {
      setValidationMode(true);
      setHasValidationErrors(true);
    } else {
      setHasValidationErrors(false);
      if (!isLastStep()) {
        dispatch(setBookingStepToAddBaggage());
        // Track booking button click (proceeding to baggage selection)
        ReactGA.event({
          category: 'FlightBooking',
          action: 'Flight Booking Continue To Baggage Selection Button Clicked',
          label: `booking_${id}`,
        });
      }
      else {
        // We need to make sure terms and conditions are accepted on final submission
        if (!allBookingFieldsValid(messages, true)) {
          setValidationMode(true);
          return;
        }
        window.scrollTo(0, 0);

        // Track booking button click (proceeding to payment)
        ReactGA.event({
          category: 'FlightBooking',
          action: 'Flight Booking Submit Passenger Details And Pay Button Clicked',
          label: `booking_${id}`,
        });

        dispatch(setBookingStepToPaymentRequested());
        dispatch(requestPayment({
          ...flightBookingState,
          flightPostBookingParameters: bookingParams
        }));
      }
    }
  }

  const handleBackButtonClicked = () => {
    // track back button click
    ReactGA.event({
      category: 'FlightBooking',
      action: 'Flight Booking Details Page Back Button Clicked',
      label: `booking_${id}`,
    });

    if (bookingStep === BOOKING_STEP.BAGGAGE_ADDITION) dispatch(setBookingStepToPassengerDetailsCapturing());

    if (bookingStep === BOOKING_STEP.PAYMENT_REQUESTED && extraBaggageOffersAvailable
    ) dispatch(setBookingStepToAddBaggage());
    else dispatch(setBookingStepToPassengerDetailsCapturing());
  }

  const handleOnBaggageChange = (passengerId: string, affectedLegIds: string[], bags: Bag[] | undefined) => {
    // track baggage selection change
    ReactGA.event({
      category: 'FlightBooking',
      action: 'Flight Booking Baggage Selection Changed',
      label: `booking_${id}_passenger_${passengerId}`,
    });

    const copiedBookingParams = deepCopy(bookingParams);
    const passengerFilter = copiedBookingParams.passengers?.filter(pass => pass.id === passengerId);
    if (passengerFilter?.length) {
      const passenger = passengerFilter[0];
      if (passenger?.extraBaggage) {
        // Clear all bags for this passenger
        Object.keys(passenger.extraBaggage!).forEach(legIds => {
          const changedBagsByLegIds: ExtraBags = passenger.extraBaggage![legIds];
          if (changedBagsByLegIds) {
            changedBagsByLegIds.available.forEach(b => {
              b.selected = false;
            });
            // clear cart
            changedBagsByLegIds.inCart.length = 0;
          }
        });

        // For every bag from bags, find the associated bag, if available update cart 
        // and mark it as selected
        affectedLegIds.forEach(legIds => {
          if (bags) {
            bags.forEach(bag => {
              // Find the correct ExtraBags by legIds
              const changedBagsByLegIds = passenger.extraBaggage![legIds];
              if (changedBagsByLegIds) {
                const bagFromValue = changedBagsByLegIds.available.find(b => b.bagNumber === bag?.bagNumber && !b.selected);
                if (bagFromValue) {
                  bagFromValue.selected = true;
                  bagFromValue.legIds = Array.isArray(bag.legIds) ? bag.legIds : [bag.legIds];
                  changedBagsByLegIds.inCart.push(bagFromValue);
                }
              }
            });
          }
        });
      }
      setBookingParams(copiedBookingParams);
    }
  }


  let itinerarySummaryCard;
  let contactDetailsForm;
  let passengerDetailsForm;
  let passengerPriceSummary;
  let addExtraBuggage;
  let globalValidationErrors;
  let termsAndContions;
  let tripOutboundLegs: Leg[] = [];
  let fareRulesModal;

  if (flightBookingDetails) {
    tripOutboundLegs = flightBookingDetails.response.offers[0].itineraries.outbound.legs;

    itinerarySummaryCard = (
      <Paper elevation={3} style={{ padding: '0', borderRadius: 20 }}>
        <FlightCardItemDetailsCollapsible
          offer={flightBookingDetails.response.offers[0]}
          passengerPricing={flightBookingDetails.response.offers[0].passengerPricing}
        />
      </Paper>
    );

    contactDetailsForm = (
      <Paper elevation={3} style={{ padding: '0', borderRadius: 20 }}>
        <TravelerContactDetails
          errors={validationMode && validationMessages?.tripContactPersons?.length ? validationMessages?.tripContactPersons[0].fields : null}
          handleFieldChange={handleContactChange}
          contactDetails={bookingParams?.tripContactPersons ? bookingParams.tripContactPersons[0].contactDetails : undefined}
          bookingContext={flightBookingDetails.bookingContext}
        />
      </Paper>
    );

    passengerDetailsForm = (
      <Paper elevation={3} style={{ padding: '0', borderRadius: 20 }}>
        <PassengerDetails
          errors={validationMode ? validationMessages.passengers : null}
          passengers={bookingParams.passengers || []}
          handleFieldChange={handlePassengerFieldChange}
          bookingContext={flightBookingDetails.bookingContext} />
      </Paper>
    );

    passengerPriceSummary = <FlightBookingPriceSummary
      passengers={bookingParams.passengers || []}
      passengerPricing={flightBookingDetails.response.offers[0].passengerPricing}
      currency={flightBookingDetails.response.offers[0].price.currency}
      agentFeesOnly={flightBookingDetails.agentFeesOnly}
      agentFees={flightBookingDetails.agentFees}
      bookingContext={flightBookingDetails.bookingContext}
    />

    // login modal removed — continue as guest
    if (flightBookingDetails.response.fareRules && Object.keys(flightBookingDetails.response.fareRules).length > 0) {
      fareRulesModal = <FairRulesModal
        onClose={handleFareRulesCloseModal}
        open={isFareRulesModalOpen}
        itinerary={flightBookingDetails.response.offers[0].itineraries}
        fareRules={flightBookingDetails.response.fareRules}
      />;
    }


    if (extraBaggageOffersAvailable && bookingParams.passengers) {
      const outboundLegs = bookingParams.order?.offers[0]?.itineraries?.outbound?.legs || [];
      const inboundLegs = bookingParams.order?.offers[0]?.itineraries?.inbound ? bookingParams.order?.offers[0]?.itineraries?.inbound?.legs : [];

      addExtraBuggage = (
        <Paper elevation={3} style={{ padding: '0', borderRadius: 20 }}>
          <AddBaggageToBookingSingleSelector
            passengers={bookingParams.passengers}
            legs={[...outboundLegs, ...inboundLegs]}
            currency={flightBookingDetails.response.offers[0].price.currency}
            onBaggageChange={handleOnBaggageChange}
          />
        </Paper>
      )
    }

    termsAndContions = (
      <Paper elevation={3} style={{ padding: 0, borderRadius: 20 }}>
        <BookingTermsAndConditions
          onAccept={handleTermsAccept}
          onFareRulesClick={handleFareRulesOpenModal}
          accepted={bookingParams.termsAccepted}
          errorMessages={validationMode ? validationMessages.termsAccepted : []}
          bookingContext={flightBookingDetails.bookingContext}
        />
      </Paper>
    );

    if (validationMessages.globalErrorMessages.length) {
      globalValidationErrors = validationMessages.globalErrorMessages.map(err => <Alert severity="error" sx={{ marginTop: 2 }} >{err}</Alert>)
    }
  }

  let bannerContent;
  let bookingContent;

  if (status === STATUS.RUNNING) {
    bookingContent = (
      <Grid container spacing={5}>
        <Grid item xs={9}>
          <Grid container spacing={5}>
            {[150, 150, 150].map((h, i) => <Grid item xs={12} key={i}><Skeleton variant="rectangular" width='100%' height={h} /></Grid>)}
          </Grid>
        </Grid>
        <Grid item xs={3}>
          <Skeleton variant="rectangular" width='100%' height={200} />
        </Grid>
      </Grid>
    );

    bannerContent = (
      <Grid container spacing={5}>
        <Grid item xs={2}></Grid>
        <Grid item xs={2}><Skeleton sx={{ bgcolor: '#fff' }} variant="circular" width={15} height={15} /></Grid>
        <Grid item xs={2}><Skeleton sx={{ bgcolor: '#fff' }} variant="circular" width={15} height={15} /></Grid>
        <Grid item xs={2}><Skeleton sx={{ bgcolor: '#fff' }} variant="circular" width={15} height={15} /></Grid>
        <Grid item xs={2}></Grid>
      </Grid>
    );
  }
  else if (status === STATUS.SUCCESS) {
    bookingContent = (
      <>
        <Box borderRadius={5}>
          <Grid container spacing={3}>
            <Grid item xs={isSmallScreen ? 12 : 9}>
              {
                hasValidationErrors && (
                  <Alert severity="error" sx={{ marginBottom: 2 }}>
                    Please review and correct the fields below to continue.
                  </Alert>
                )
              }
              <Box>
                {itinerarySummaryCard}
              </Box>
              {bookingStep === BOOKING_STEP.PASSENGER_DETAILS_CAPTURING && (
                <Collapse in={bookingStep === BOOKING_STEP.PASSENGER_DETAILS_CAPTURING} timeout={1000}>
                  <Box>
                    {globalValidationErrors}
                    {passengerDetailsForm}
                  </Box>
                  <Box>
                    {contactDetailsForm}
                  </Box>
                </Collapse>
              )}
              {bookingStep === BOOKING_STEP.BAGGAGE_ADDITION && (
                <Collapse in={bookingStep === BOOKING_STEP.BAGGAGE_ADDITION} timeout={1000}>
                  <Box>
                    {addExtraBuggage}
                  </Box>
                </Collapse>
              )}
              {isLastStep() ? termsAndContions : ''}
              <Box mt={2}>
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    textAlign={{ xs: 'center', sm: 'left' }}
                    mb={{ xs: 2, sm: 0 }}
                  >
                    {
                      (bookingStep === BOOKING_STEP.BAGGAGE_ADDITION || bookingStep === BOOKING_STEP.PAYMENT_REQUESTED) && (
                        <Button
                          startIcon={<ArrowBackIcon />}
                          onClick={handleBackButtonClicked}
                          fullWidth
                          sx={{
                            width: { xs: '100%', sm: 'auto' },
                          }}
                          color="secondary"
                        >
                          Back
                        </Button>
                      )}
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    textAlign={{ xs: 'center', sm: 'right' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={handleBookingDetailsSubmitted}
                      disabled={!bookingParams.termsAccepted && isLastStep() && validationMode}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                      }}
                    >
                      {isLastStep() ? 'Confirm & Pay' : 'Continue'}
                    </Button>
                  </Grid>

                </Grid>
              </Box>
            </Grid>
            <Grid item xs={isSmallScreen ? 12 : 3} order={isSmallScreen ? -1 : 0}>
              {passengerPriceSummary}
            </Grid>
          </Grid>
        </Box>
      </>
    );

    bannerContent = (
      tripOutboundLegs.length &&
      <Box sx={{ color: '#fff' }}>
        <Stepper
          sx={{
            width: {
              xs: '100%',
              sm: '60%',
              margin: '0 auto',
            },
          }}
        >
          <Step><Typography variant="h4" sx={{ fontSize: { xs: 20, sm: 28, md: 34 } }}>{tripOutboundLegs[0].departure.city.friendlyName}</Typography></Step>
          <Step><Flight sx={{ fontSize: { xs: 16, sm: 20 }, transform: 'rotate(45deg)', marginTop: .5 }} /></Step>
          <Step><Typography variant="h4" sx={{ fontSize: { xs: 20, sm: 28, md: 34 } }}>{tripOutboundLegs[tripOutboundLegs.length - 1].arrival.city.friendlyName}</Typography></Step>
        </Stepper>
      </Box>
    )
  }


  let content = (
    <StandardPage
      bannerContent={bannerContent}
    >
      {status === STATUS.SUCCESS && flightBookingDetails && tripOutboundLegs.length > 0 ? (
        <Helmet>
          <title>{`${tripOutboundLegs[0].departure.city.friendlyName} to ${tripOutboundLegs[tripOutboundLegs.length - 1].arrival.city.friendlyName} ${flightBookingDetails.bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY ? '| Create Itinerary' : ' | Flight Booking'}`}</title>
          <meta name="description" content={`${flightBookingDetails.bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY ? 'Create a travel itinerary' : 'Book your flight'} from ${tripOutboundLegs[0].departure.city.friendlyName} to ${tripOutboundLegs[tripOutboundLegs.length - 1].arrival.city.friendlyName}. Find the best deals and easy booking process with Tripenger.`} />
        </Helmet>
      ) : status === STATUS.ERROR ? (
        <Helmet>
          <title>No Flights Available - Flight Search Results</title>
          <meta name="description" content="We couldn't find any flights matching your search criteria. Please try different dates or destinations." />
        </Helmet>
      ) : (
        <Helmet>
          <title>Loading Flight Details...</title>
          <meta name="description" content="Please wait while we load your flight booking details." />
        </Helmet>
      )}
      {fareRulesModal}
      {bookingContent}
    </StandardPage>
  );

  if (status === STATUS.ERROR && [ERROR_MESSAGE.PAGE_NOT_FOUND, ERROR_MESSAGE.UNKOWN_ERROR].indexOf(flightBookingState.errorMessage || '') > -1) {
    content = <PageNotFound />
  }

  return content;
}

export default FlightBooking;